/**
 * Python script generator for macro recordings.
 *
 * Assembles complete, runnable Python scripts from recorded actions
 * using the templates module.
 */

import { getRecordedActions } from '../state/macro.js';
import {
  SCRIPT_HEADER,
  DEX_CLIENT_CLASS,
  generateArgparser,
  generateMainFunction
} from './templates.js';

/**
 * Generate a complete Python script from recorded actions.
 *
 * The script includes:
 * - Header with imports and usage documentation
 * - DEXClient class with HTTP and crypto methods
 * - Argparser tailored to the recorded operations
 * - Main function with code for each recorded action
 *
 * @param {string|null} apiGatewayUrl - API Gateway URL to embed as --base-url default (null = required)
 * @returns {string} Complete Python script
 */
export function generatePythonScript(apiGatewayUrl = null) {
  const actions = getRecordedActions();

  // Analyze actions to determine what's needed
  const analysis = analyzeActions(actions);

  // Pass API Gateway URL as default for --base-url
  analysis.defaultBaseUrl = apiGatewayUrl;

  // Generate argparser based on analysis
  const argparserCode = generateArgparser(analysis);

  // Generate main function from actions
  const mainCode = generateMainFunction(actions);

  // Assemble the complete script
  return SCRIPT_HEADER + DEX_CLIENT_CLASS + argparserCode + mainCode;
}

/**
 * Analyze recorded actions to determine script requirements.
 *
 * @param {Array} actions - Array of recorded action objects
 * @returns {Object} Analysis results for argparser generation
 */
function analyzeActions(actions) {
  const result = {
    hasPutPayload: false,
    hasGetPayload: false,
    hasMarkComplete: false,
    sampleIds: new Set(),
    stageIds: new Set(),
    workflowIds: new Set(),
    readerIds: new Set()
  };

  for (const action of actions) {
    const { operation, params = {} } = action;

    // Track operation types
    if (operation === 'put_payload') {
      result.hasPutPayload = true;
      if (params.readerId) {
        result.readerIds.add(params.readerId);
      }
    } else if (operation === 'get_payload') {
      result.hasGetPayload = true;
    } else if (operation === 'mark_complete') {
      result.hasMarkComplete = true;
    }

    // Track identifiers
    if (params.sampleId) result.sampleIds.add(params.sampleId);
    if (params.stageId) result.stageIds.add(params.stageId);
    if (params.workflowId) result.workflowIds.add(params.workflowId);
  }

  return result;
}

/**
 * Generate a human-readable summary of recorded actions.
 *
 * Useful for preview UI to show users what's been recorded
 * without showing code.
 *
 * @returns {Array<string>} Array of human-readable action descriptions
 */
export function generateActionSummary() {
  const actions = getRecordedActions();

  if (actions.length === 0) {
    return ['No actions recorded yet.'];
  }

  return actions.map((action, index) => {
    const { operation, params, success, error } = action;
    const num = index + 1;

    let description = '';

    if (operation === 'put_payload') {
      const reader = params.readerId || 'unknown reader';
      const sample = params.sampleId || 'unknown';
      const stage = params.stageId || 'unknown';
      description = `Send payload to reader '${reader}' (sample: ${sample}, stage: ${stage})`;
      if (params.workflowId) {
        description += ` [workflow: ${params.workflowId}]`;
      }
    } else if (operation === 'get_payload') {
      const sample = params.sampleId || 'unknown';
      const stage = params.stageId || 'unknown';
      description = `Retrieve payload (sample: ${sample}, stage: ${stage})`;
      if (params.workflowId) {
        description += ` [workflow: ${params.workflowId}]`;
      }
    } else if (operation === 'mark_complete') {
      const sample = params.sampleId || 'unknown';
      const stage = params.stageId || 'unknown';
      description = `Mark payload complete (sample: ${sample}, stage: ${stage})`;
      if (params.workflowId) {
        description += ` [workflow: ${params.workflowId}]`;
      }
    } else {
      description = `Unknown operation: ${operation}`;
    }

    // Add failure indicator
    if (!success) {
      description += ` [FAILED: ${error || 'Unknown error'}]`;
    }

    return `${num}. ${description}`;
  });
}

/**
 * Get metadata about the current recording.
 *
 * @returns {Object} Recording metadata
 */
export function getRecordingMetadata() {
  const actions = getRecordedActions();
  const analysis = analyzeActions(actions);

  return {
    actionCount: actions.length,
    successCount: actions.filter(a => a.success).length,
    failureCount: actions.filter(a => !a.success).length,
    hasPutPayload: analysis.hasPutPayload,
    hasGetPayload: analysis.hasGetPayload,
    hasMarkComplete: analysis.hasMarkComplete,
    uniqueSamples: analysis.sampleIds.size,
    uniqueStages: analysis.stageIds.size,
    uniqueReaders: analysis.readerIds.size
  };
}
